// src/App.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import { FaMoon, FaSun } from "react-icons/fa";

import Cursor from "./components/Cursor";
import NavDots from "./components/NavDots";
import HomePage from "./components/HomePage";
import WorkExpPage from "./components/WorkExpPage";
import AboutMePage from "./components/AboutMePage";
import ContactPage from "./components/ContactPage";
import { playClickSound } from "./util/playClickSound";

const TOTAL_PAGES = 4;

function App() {
  const [currentPage, setCurrentPage] = useState(0);
  const [logoClicks, setLogoClicks] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("theme-mode");
    if (saved === "dark") return true;
    if (saved === "light") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  const isScrolling = useRef(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartedInScrollable = useRef(false);
  const wheelAccumulator = useRef(0);
  const wheelResetTimer = useRef(null);
  const containerRef = useRef(null);

  const goToPage = useCallback((pageIndex) => {
    if (pageIndex >= 0 && pageIndex < TOTAL_PAGES) {
      setCurrentPage(pageIndex);
    }
  }, []);

  // Logo click easter egg
  const handleLogoClick = () => {
    setLogoClicks((prev) => prev + 1);
  };

  useEffect(() => {
    const handler = (e) => {
      if (
        e.target.matches(
          ".nav-link, .gallery-nav, .location-tag, .social-link, .social-icon, .cta-button, .resume-link, .contact-link"
        )
      ) {
        playClickSound();
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  // Block pinch-zoom, ctrl/cmd+wheel zoom, and double-tap zoom on mobile
  useEffect(() => {
    const preventPinch = (e) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const preventMultiTouchMove = (e) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const preventCtrlWheel = (e) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };

    let lastTouchEnd = 0;
    const preventDoubleTap = (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };

    document.addEventListener("touchstart", preventPinch, { passive: false });
    document.addEventListener("touchmove", preventMultiTouchMove, {
      passive: false,
    });
    document.addEventListener("wheel", preventCtrlWheel, { passive: false });
    document.addEventListener("touchend", preventDoubleTap, { passive: false });
    // iOS Safari gesture events
    const gestureStart = (e) => e.preventDefault();
    const gestureChange = (e) => e.preventDefault();
    const gestureEnd = (e) => e.preventDefault();
    document.addEventListener("gesturestart", gestureStart, { passive: false });
    document.addEventListener("gesturechange", gestureChange, {
      passive: false,
    });
    document.addEventListener("gestureend", gestureEnd, { passive: false });

    return () => {
      document.removeEventListener("touchstart", preventPinch);
      document.removeEventListener("touchmove", preventMultiTouchMove);
      document.removeEventListener("wheel", preventCtrlWheel);
      document.removeEventListener("touchend", preventDoubleTap);
      document.removeEventListener("gesturestart", gestureStart);
      document.removeEventListener("gesturechange", gestureChange);
      document.removeEventListener("gestureend", gestureEnd);
    };
  }, []);

  useEffect(() => {
    if (logoClicks === 3) {
      document.body.classList.add("rainbow-body");
      const timer = setTimeout(() => {
        document.body.classList.remove("rainbow-body");
        setLogoClicks(0);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [logoClicks]);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", isDarkMode);
    localStorage.setItem("theme-mode", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  // Event listeners for navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        goToPage(currentPage + 1);
      } else if (e.key === "ArrowLeft") {
        goToPage(currentPage - 1);
      }
    };

    const handleWheel = (e) => {
      // Never allow wheel side-effects while horizontal page transition is active.
      if (isScrolling.current) {
        if (e.cancelable) {
          e.preventDefault();
        }
        return;
      }

      // If user is interacting with the timeline list, reserve wheel for timeline scrolling.
      if (e.target instanceof Element && e.target.closest(".timeline-scroll")) {
        return;
      }

      // Accumulate wheel deltas to treat trackpad gestures as a single navigation
      if (e.cancelable) {
        e.preventDefault();
      }

      const delta = e.deltaY || 0;
      wheelAccumulator.current += delta;

      // Reset accumulator if there's a pause in wheel events (end of gesture)
      if (wheelResetTimer.current) {
        clearTimeout(wheelResetTimer.current);
      }
      wheelResetTimer.current = setTimeout(() => {
        wheelAccumulator.current = 0;
      }, 200);

      const threshold = 120; // Higher threshold to avoid accidental multi-page scrolls
      if (Math.abs(wheelAccumulator.current) >= threshold) {
        if (wheelAccumulator.current > 0) {
          goToPage(currentPage + 1);
        } else {
          goToPage(currentPage - 1);
        }
        isScrolling.current = true;
        wheelAccumulator.current = 0;

        // Fallback unlock in case no transitionend is fired
        setTimeout(() => {
          isScrolling.current = false;
        }, 700);
      }
    };

    const handleTouchStart = (e) => {
      const touch = e.changedTouches[0];
      touchStartX.current = touch.screenX;
      touchStartY.current = touch.screenY;
      touchStartedInScrollable.current = Boolean(
        e.target instanceof Element && e.target.closest(".timeline-scroll")
      );
    };

    const handleTouchEnd = (e) => {
      // If touch began inside scrollable timeline, do not interpret it as page swipe.
      if (touchStartedInScrollable.current) {
        touchStartedInScrollable.current = false;
        return;
      }

      const touch = e.changedTouches[0];
      const diffX = touchStartX.current - touch.screenX;
      const diffY = touchStartY.current - touch.screenY;
      const horizontalThreshold = 50;
      const verticalTolerance = 28;

      // Only trigger horizontal navigation for clearly horizontal gestures.
      if (
        Math.abs(diffX) > horizontalThreshold &&
        Math.abs(diffY) < verticalTolerance
      ) {
        if (diffX > 0) {
          goToPage(currentPage + 1); // Swipe left
        } else {
          goToPage(currentPage - 1); // Swipe right
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
      if (wheelResetTimer.current) {
        clearTimeout(wheelResetTimer.current);
        wheelResetTimer.current = null;
      }
    };
  }, [currentPage, goToPage]);

  return (
    <>
      <Cursor />
      <button
        type="button"
        className="theme-toggle"
        onClick={() => setIsDarkMode((prev) => !prev)}
        aria-label="Toggle dark mode"
      >
        {isDarkMode ? <FaSun aria-hidden="true" /> : <FaMoon aria-hidden="true" />}
        <span>{isDarkMode ? "LIGHT" : "DARK"}</span>
      </button>
      <NavDots
        totalPages={TOTAL_PAGES}
        currentPage={currentPage}
        goToPage={goToPage}
      />

      <div
        ref={containerRef}
        className="container"
        onTransitionEnd={(e) => {
          if (e.target === e.currentTarget && e.propertyName === "transform") {
            isScrolling.current = false;
          }
        }}
        style={{ transform: `translateX(-${currentPage * 100}vw)` }}
      >
        <HomePage goToPage={goToPage} onLogoClick={handleLogoClick} />
        <AboutMePage goToPage={goToPage} isActive={currentPage === 1} />
        <WorkExpPage isActive={currentPage === 2} />
        <ContactPage isActive={currentPage === 3} />
      </div>
    </>
  );
}

export default App;
